import { useEffect, useState } from 'react'

const INPUT_ELEMENTS = ['INPUT', 'TEXTAREA', 'SELECT'] as const

export function useKeyboardVisibility() {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)
  const [initialHeight, setInitialHeight] = useState(0)

  useEffect(() => {
    // 初期の高さを記録
    setInitialHeight(window.innerHeight)
  }, [])

  useEffect(() => {
    let focusTimeout: NodeJS.Timeout

    const isInputElement = (element: Element | null): boolean => {
      return element !== null && INPUT_ELEMENTS.includes(element.tagName as any)
    }

    const updateKeyboardState = (visible: boolean) => {
      setIsKeyboardVisible(visible)
    }

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as Element
      if (isInputElement(target)) {
        clearTimeout(focusTimeout)
        updateKeyboardState(true)
      }
    }

    const handleFocusOut = () => {
      clearTimeout(focusTimeout)
      focusTimeout = setTimeout(() => {
        if (!isInputElement(document.activeElement)) {
          updateKeyboardState(false)
        }
      }, 100)
    }

    // より積極的なビューポート監視
    const handleViewportChange = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight
      const heightRatio = currentHeight / initialHeight
      
      // デバッグ用
      console.log('Viewport change:', {
        currentHeight,
        initialHeight,
        ratio: heightRatio,
        activeElement: document.activeElement?.tagName
      })
      
      // 高さが20%以上減少した場合、またはinput要素にフォーカスがある場合
      if (heightRatio < 0.8 || isInputElement(document.activeElement)) {
        updateKeyboardState(true)
      } else if (heightRatio > 0.95 && !isInputElement(document.activeElement)) {
        updateKeyboardState(false)
      }
    }

    // インプット要素のクリックも監視
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element
      if (isInputElement(target)) {
        updateKeyboardState(true)
      }
    }

    // イベントリスナーの登録
    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('focusout', handleFocusOut)
    document.addEventListener('click', handleClick)
    window.addEventListener('resize', handleViewportChange)
    window.visualViewport?.addEventListener('resize', handleViewportChange)
    window.visualViewport?.addEventListener('scroll', handleViewportChange)

    // 初回チェック
    if (isInputElement(document.activeElement)) {
      updateKeyboardState(true)
    }

    // クリーンアップ
    return () => {
      clearTimeout(focusTimeout)
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
      document.removeEventListener('click', handleClick)
      window.removeEventListener('resize', handleViewportChange)
      window.visualViewport?.removeEventListener('resize', handleViewportChange)
      window.visualViewport?.removeEventListener('scroll', handleViewportChange)
    }
  }, [initialHeight])

  // 開発環境でのデバッグ用
  if (typeof window !== 'undefined' && (window as any).forceKeyboardVisible) {
    return true
  }

  return isKeyboardVisible
}